// @ts-check
import { HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import { Tabs } from '@/js/enums/externalRepairParts';
import {
  callFetchActiveWorkers,
  callFetchExpectedWorkers,
  callFetchInactiveWorkers,
  callFetchWorkPartDetail,
  callFetchWorkParts,
} from '@/js/services/repairs/ApiRepairs';
import { useRepairPartsStore } from '@/js/stores/repairParts';
import { logError } from '@/js/utils/log';

import { useParamRef } from '@erpagroapp/params';
import { useGeolocation } from '@vueuse/core';
import { f7 } from 'framework7-vue';
import { storeToRefs } from 'pinia';
import { computed, readonly } from 'vue';

export function useRepairParts() {
  const {
    workParts,
    isLoadingWorkParts,
    currentTab,
    selectedDate,
    worker,
    workPartDetails,
  } = storeToRefs(useRepairPartsStore());

  const isAllowedMultipleDaysRouteOperation = useParamRef('PermitirTrayectosMultiplesDiasParteReparacion');
  const { coords } = useGeolocation();

  const isSelectedDateToday = computed(() => {
    if (!selectedDate.value) {
      return false;
    }

    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    const currentTime = currentDate.getTime();

    return selectedDate.value.Init.getTime() === currentTime
      && selectedDate.value.End.getTime() === currentTime;
  });

  const isAllowedToDoRouteOperation = computed(() => isAllowedMultipleDaysRouteOperation.value || isSelectedDateToday.value);

  /**
   * Checks if the current user is responsible for the work part.
   * @param {number} responsibleWorkerId - The ID of the responsible worker.
   * @returns {boolean} `true` if the user is responsible, `false` otherwise.
   */
  function isUserResponsibleWorkPart(responsibleWorkerId) {
    return !!responsibleWorkerId
      && !!worker.value
      && responsibleWorkerId === worker.value.Id;
  }

  async function loadWorkParts() {
    if (!worker.value) {
      throw new Error('Worker is not defined.');
    }

    if (!selectedDate.value) {
      return;
    }

    const pending = currentTab.value === Tabs.PENDING;
    const workPartsMessage = pending ? 'partes de trabajo' : 'partes de trabajo finalizados';

    workParts.value = [];

    try {
      isLoadingWorkParts.value = true;

      const latitude = Number.isFinite(coords.value.latitude) ? coords.value.latitude : 0;
      const longitude = Number.isFinite(coords.value.longitude) ? coords.value.longitude : 0;

      const { data } = await callFetchWorkParts(
        worker.value.Id,
        selectedDate.value.Init.toISOString(),
        selectedDate.value.End.toISOString(),
        !pending,
        latitude,
        longitude,
      );
      isLoadingWorkParts.value = false;

      if (Array.isArray(data)) {
        workParts.value = data;
      } else {
        HiNotification.Toast(data.Mensaje || `No se han podido obtener los ${workPartsMessage}`);
      }
    } catch (error) {
      logError(error);

      isLoadingWorkParts.value = false;
      HiNotification.Toast(`No se han podido obtener los ${workPartsMessage}`);
    }
  }

  /**
   * Loads the active workers for a specific repair part.
   * @param {number} workPartId - The ID of the work part.
   * @returns {Promise<import('@erpagroapp/types').Trabajador[]>} The active workers.
   */
  async function loadActiveWorkers(workPartId) {
    HiDialog.Preloader(true, 'Cargando los trabajadores activos...');

    try {
      const { data } = await callFetchActiveWorkers(workPartId);
      return data;
    } catch (error) {
      logError(error);
      HiNotification.Toast('No se han podido obtener los trabajadores activos para este parte');
      return [];
    } finally {
      HiDialog.Preloader(false);
    }
  }

  /**
   * Loads the inactive workers for a specific repair part.
   * @param {number} workPartId - The ID of the work part.
   * @returns {Promise<import('@erpagroapp/types').Trabajador[]>} The inactive workers.
   */
  async function loadInactiveWorkers(workPartId) {
    HiDialog.Preloader(true, 'Cargando los trabajadores inactivos...');

    try {
      const { data } = await callFetchInactiveWorkers(workPartId);
      return data;
    } catch (error) {
      logError(error);
      HiNotification.Toast('No se han podido obtener los trabajadores inactivos para este parte');
      return [];
    } finally {
      HiDialog.Preloader(false);
    }
  }

  /**
   * Loads the expected workers for a specific repair part.
   * @param {number} workPartId - The ID of the work part.
   * @returns {Promise<import('@erpagroapp/types').Trabajador[]>} The expected workers.
   */
  async function loadExpectedWorkers(workPartId) {
    HiDialog.Preloader(true, 'Cargando los trabajadores previstos...');

    try {
      const { data } = await callFetchExpectedWorkers(workPartId);
      return data;
    } catch (error) {
      logError(error);
      return [];
    } finally {
      HiDialog.Preloader(false);
    }
  }

  /**
   * Navigates to the details of a specific work part.
   * @param {number} workPartId - The ID of the work part.
   */
  function navigateToPartDetails(workPartId) {
    f7.views.main.router.navigate('/repairs/repairParts/details', {
      props: {
        workPartId,
      },
    });
  }

  /**
   * Navigates to the details of a specific work part.
   * @param {number} workPartId - The ID of the work part.
   * @param {number} signType - The type of sign.
   */
  function navigateToSignPage(workPartId, signType) {
    f7.views.main.router.navigate('/repairs/repairParts/sign', {
      props: {
        workPartId,
        signType,
      },
    });
  }

  /**
   * Fetches work part details.
   * @param {number} workPartId - The ID of the work part.
   * @returns {Promise<boolean>} `true` if the operation was successful, `false` otherwise.
   */
  async function fetchWorkPartDetails(workPartId) {
    if (!worker.value) {
      throw new Error('Worker is not defined.');
    }

    let successful = false;

    try {
      const { data } = await callFetchWorkPartDetail(
        worker.value.Id,
        workPartId,
      );

      if (data.Valor === undefined) {
        [workPartDetails.value] = data.Table1;
        successful = true;
      } else {
        HiNotification.Toast(data.Mensaje || 'No se ha podido obtener detalle del parte');
      }
    } catch (error) {
      logError(error);
      HiNotification.Toast('No se ha podido obtener detalle del parte');
    }

    return successful;
  }

  return {
    workParts: readonly(workParts),
    isLoadingWorkParts: readonly(isLoadingWorkParts),
    selectedDate,
    currentTab,
    isAllowedToDoRouteOperation,
    worker,
    workPartDetails: readonly(workPartDetails),
    isUserResponsibleWorkPart,
    loadWorkParts,
    loadActiveWorkers,
    loadInactiveWorkers,
    loadExpectedWorkers,
    navigateToPartDetails,
    navigateToSignPage,
    fetchWorkPartDetails,
  };
}
